import { useState } from 'react'
import { Outlet } from 'react-router-dom'
import { Stack, Alert } from '@mui/material'
import { useStytchIsAuthorized } from '@stytch/react/b2b'
import { useFeatureFlagContext } from '@/feature_flags/FeatureFlagProvider'
import useOverlay from '@/hooks/useOverlay'
import useProjectFeatureEnabled from '@/hooks/useProjectFeatureEnabled'
import NotFound from '@/pages/NotFound'
import { useGetProjectTemplates } from '@/service-library/hooks/project-templates'
import { useGetPubModels } from '@/service-library/hooks/pub-models'
import useIsSuperUser from '@/services/hooks/useIsSuperUser'
import { notNull } from '@/utils/typescript'
import Drawer from '@/components/drawer/Drawer'
import ProjectBreadcrumb from '@/components/project-tables/ProjectBreadcrumb'
import { useProjectContext } from '@/components/project-tables/ProjectProvider'
import SettingsNavigation from '@/components/settings-navigation/SettingsNavigation'
import { useSelectedWorkflowContext } from '@/components/workflows/SelectedWorkflowProvider'
import useLineItemRollupAvailable from './line-item-rollup/useLineItemRollupAvailable'

const qualityRoutes: RouteDefinition[] = [
  {
    to: 'document-rules',
    label: 'Document Rules',
    keepQueryParams: true,
  },
  {
    to: 'field-rules',
    label: 'Field Rules',
    keepQueryParams: true,
  },
]

const exportRoutes: RouteDefinition[] = [
  {
    to: 'line-item-rollup',
    label: 'Line Item Rollup',
    keepQueryParams: true,
  },
]

type RouteDefinition = {
  to: string
  label: string
  isSuperUserOnly?: boolean
  keepQueryParams?: boolean
}

export default function ProjectSettingsLayout() {
  const { project } = useProjectContext()
  const { isAuthorized, isInitialized } = useStytchIsAuthorized(
    'pd.project',
    'edit',
  )
  const isSuperUser = useIsSuperUser()
  const featureFlags = useFeatureFlagContext()
  const { isAvailable: isRollupAvailable } = useLineItemRollupAvailable({
    enabled: featureFlags.rollup_configs,
  })
  const { selectedWorkflow, workflows } = useSelectedWorkflowContext()
  const [errors, setErrors] = useState<string[] | null>(null)
  const drawerOverlay = useOverlay({ initiallyOpen: true })

  const { isEnabled: isPurchaseOrdersEnabled } =
    useProjectFeatureEnabled('purchase-orders')

  const { projectTemplates } = useGetProjectTemplates({
    filters: {
      limit: '1',
      project_id: project.id,
    },
  })

  const { pubModels } = useGetPubModels({
    filters: {
      project_model_id: selectedWorkflow?.project_model_id || undefined,
      fields__only: 'id',
      limit: '1',
    },
    enabled: !!selectedWorkflow?.project_model_id,
  })

  // Model Tables lists every model on the project, so any model workflow
  // counts here — not just the selected one.
  const hasModelWorkflow = workflows.some(
    (workflow) => !!workflow.project_model_id,
  )

  const routes: RouteDefinition[] = [
    {
      to: 'general',
      label: 'General',
      keepQueryParams: true,
    },
    {
      to: 'fields',
      label: 'Fields',
      keepQueryParams: true,
    },
    isPurchaseOrdersEnabled
      ? {
          to: 'purchase-orders',
          label: 'Purchase Order Matching',
          keepQueryParams: true,
        }
      : null,
    {
      to: 'tags',
      label: 'Document Tags',
      keepQueryParams: true,
    },
    featureFlags.positions
      ? {
          to: 'positions',
          label: 'Positions',
          keepQueryParams: true,
        }
      : null,
    hasModelWorkflow
      ? {
          to: 'model-tables',
          label: 'Model Tables',
          keepQueryParams: true,
        }
      : null,
  ].filter(Boolean) as RouteDefinition[]

  const workflowRoutes: RouteDefinition[] = [
    {
      to: 'workflow-states',
      label: 'Workflow States',
      keepQueryParams: true,
    },
    selectedWorkflow.project_model_id
      ? {
          to: 'model-configuration',
          label: 'Model Configuration',
          keepQueryParams: true,
        }
      : null,
    featureFlags.show_approvals
      ? {
          to: 'approvals',
          label: 'Approvals',
          keepQueryParams: true,
        }
      : null,
  ].filter(notNull)

  const superUserRoutes: RouteDefinition[] = [
    {
      to: 'features',
      label: 'Features',
      keepQueryParams: true,
      isSuperUserOnly: true,
    },
    {
      to: 'model-library',
      label: 'Model Library',
      keepQueryParams: true,
      isSuperUserOnly: true,
    },
    projectTemplates.length > 0
      ? {
          to: 'template',
          label: 'Template',
          keepQueryParams: true,
          isSuperUserOnly: true,
        }
      : null,
    {
      to: 'advanced-settings',
      label: 'Advanced Settings',
      keepQueryParams: true,
      isSuperUserOnly: true,
    },
    pubModels.length > 0
      ? {
          to: 'published-model-management',
          label: 'Published Model Management',
          keepQueryParams: true,
          isSuperUserOnly: true,
        }
      : null,
  ].filter(notNull)

  if (!isInitialized) return null
  if (!isAuthorized) return <NotFound />

  return (
    <>
      <ProjectBreadcrumb
        label="Settings"
        url={`/projects/${project.id}/settings/general`}
      />
      <Drawer
        overlay={drawerOverlay}
        size={isPurchaseOrdersEnabled ? 'md' : 'sm'}
        transparent
      >
        <SettingsNavigation label="Project Settings" routes={routes} />
        <SettingsNavigation label="Quality Control" routes={qualityRoutes} />
        {featureFlags.rollup_configs && isRollupAvailable && (
          <SettingsNavigation label="Export Settings" routes={exportRoutes} />
        )}
        <SettingsNavigation
          label="Workflow Settings"
          routes={workflowRoutes}
          subHeader={selectedWorkflow.name}
        />
        {isSuperUser && superUserRoutes.length > 0 && (
          <SettingsNavigation label="Admin" routes={superUserRoutes} />
        )}
      </Drawer>

      <Stack
        direction="column"
        p={2}
        pt={3}
        width="100%"
        flexGrow={1}
        sx={{ minHeight: 0 }}
      >
        {errors && <Alert severity="error">{errors}</Alert>}
        <Outlet
          context={{
            setWizardErrors: setErrors,
            isSettings: true,
          }}
        />
      </Stack>
    </>
  )
}
